import React, { useMemo } from 'react';

import { formatDate } from 'lib/helpers/dateUtils';
import { TYPE_LABELS } from 'lib/constants/transaction/typeLabels';
import { Capitalize, cn, inRn, mString } from 'lib/helpers/utility';
import { COMPLETED_TRANSACTION_STATUSES } from 'lib/constants/transaction/completedTransactionStatuses';

import Allowed from 'components/shared/Permissions/Allowed';
import DocText from 'components/shared/DocumentSheet/DocText';
import DocCard from 'components/shared/DocumentSheet/components/DocCard';
import { DATE_TIME_MEDIUM } from 'components/shared/DocumentSheet/lib/docFormats';
import { DOC_GAPS, DOC_TEXT } from 'components/shared/DocumentSheet/lib/docStyles';
import DocSectionHead from 'components/shared/DocumentSheet/components/DocSectionHead';

import { maskCardNumber } from '../../lib/cardMask';
import { calculateTotals } from '../ReceiptSummary/lib/helpers';

const CardLabel = ({ children, className }) => (
  <DocText variant="label" size="small" className={cn(DOC_TEXT.microLabel, className)}>
    {String(children).toUpperCase()}
  </DocText>
);

const PaymentSummary = ({ transaction, transactions, isPatient, timezone }) => {
  const totals = useMemo(() => calculateTotals(transactions), [transactions]);

  if (!transaction?.id) return null;

  const isCompleted = COMPLETED_TRANSACTION_STATUSES.includes(transaction?.status?.toLowerCase());

  const method = transaction?.type
    ? (TYPE_LABELS[transaction.type] ?? Capitalize(transaction.type))
    : '';
  const reference = transaction?.check_number
    ? `#${transaction.check_number}`
    : maskCardNumber(transaction?.cc_number);
  const methodValue = [method, reference].filter(Boolean).join(' ');

  const postedLine = [
    transaction?.created_at
      ? `Posted ${formatDate(transaction.created_at, timezone, DATE_TIME_MEDIUM)}`
      : '',
    `Payment ID ${transaction.id}`
  ]
    .filter(Boolean)
    .join(' · ');

  const thisPayment = inRn(transaction?.amount_paid_self);
  const remaining = inRn(totals?.totalBalance);
  const previous = remaining + thisPayment;

  const balances = [
    { label: 'Previous balance', value: mString(previous), className: 'flex-1' },
    { label: 'This payment', value: `–${mString(thisPayment)}`, className: 'flex-1' },
    {
      label: 'Remaining balance',
      value: mString(remaining),
      note: thisPayment > 0 && remaining <= 0 ? 'Paid in full' : '',
      className: 'flex-[1.3]'
    }
  ];

  return (
    <Allowed
      requiredPermissions={isPatient ? '' : 'payments.read'}
      requiredModules={isPatient ? '' : 'medipay.payment_suite'}>
      <div className={DOC_GAPS.sectionTight}>
        <DocSectionHead title="Payment summary" />
        <DocCard>
          <div className="flex items-start justify-between gap-x-6">
            <div className="min-w-0 flex-1">
              <CardLabel>Payment method</CardLabel>
              {methodValue && (
                <DocText className={cn(DOC_TEXT.cardLine, 'mt-0.5')}>
                  <span className="font-700">{methodValue}</span>
                  {transaction?.note ? ` · ${transaction.note}` : ''}
                </DocText>
              )}
              {postedLine && (
                <DocText size="xs" className={cn(DOC_TEXT.cardSub, 'mt-0.5')}>
                  {postedLine}
                </DocText>
              )}
            </div>
            <div className="shrink-0 text-right">
              <CardLabel className="text-right">{isCompleted ? 'Amount paid' : 'Amount'}</CardLabel>
              <DocText size="large" className={cn(DOC_TEXT.cardAmount, 'mt-0.5 text-right')}>
                {mString(thisPayment)}
              </DocText>
            </div>
          </div>
          <div className="my-2 h-px bg-neutral-300" />
          <div className="flex gap-x-6 xs:flex-col xs:gap-y-2.5">
            {balances.map((balance) => (
              <div key={balance.label} className={cn('min-w-0', balance.className)}>
                <CardLabel>{balance.label}</CardLabel>
                <DocText className={cn(DOC_TEXT.fieldValue, 'mt-0.5 tabular-nums')}>
                  {balance.value}
                  {balance.note ? ` · ${balance.note}` : ''}
                </DocText>
              </div>
            ))}
          </div>
        </DocCard>
      </div>
    </Allowed>
  );
};

export default PaymentSummary;
